import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { MonitorSmartphone, RefreshCw, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { useUrlState } from '../hooks/useUrlState.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { clientActivityAPI, projectAPI } from '../services/api.ts';
import {
  ClientUsage,
  EmptyState,
  PageHeader,
  SeenAt,
  TablePagination,
} from '../components/app/index.ts';

dayjs.extend(relativeTime);

const PAGE_SIZE = 25;

// Tells the profile which list to go back to.
const FROM_HERE = { from: '/user-activity' };

const CLIENTS = [
  { value: '', label: 'All' },
  { value: 'app', label: 'App' },
  { value: 'web', label: 'Web' },
  { value: 'both', label: 'Both' },
  { value: 'none', label: 'Never seen' },
];

const ACTIVE_WITHIN = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const errorOf = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * User Activity: who uses the mobile app, who the web dashboard, on which
 * version and device, and when they were last seen. An admin sees their own
 * project; a super admin sees every project and can narrow to one.
 */
export const ClientActivity = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [client, setClient] = useUrlState('client', '', { resetPage: true });
  const [activeWithin, setActiveWithin] = useUrlState('active', null, {
    resetPage: true,
  });
  const [search, setSearch] = useUrlState('search', '', { resetPage: true });
  const [debouncedSearch] = useDebouncedValue(search.trim(), 350);
  const [projectId, setProjectId] = useUrlState('project', null, {
    resetPage: true,
  });
  const [projects, setProjects] = useState<{ value: string; label: string }[]>(
    [],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientActivityAPI.list({
        page,
        limit: PAGE_SIZE,
        ...(client && { client: client as any }),
        ...(debouncedSearch && { search: debouncedSearch }),
        // "Never seen" and "active within" cannot both hold.
        ...(activeWithin && client !== 'none' && { activeWithin }),
        ...(projectId && { project_id: projectId }),
      });
      setRows(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.total || 0);
      setError('');
    } catch (err: any) {
      setError(errorOf(err, 'Failed to load user activity'));
    } finally {
      setLoading(false);
    }
  }, [page, client, debouncedSearch, activeWithin, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    projectAPI
      .getAll(1, 200)
      .then((response: any) => {
        const list = response?.data?.projects || response?.data || [];
        setProjects(
          (Array.isArray(list) ? list : []).map((project: any) => ({
            value: String(project.id),
            label: project.name,
          })),
        );
      })
      .catch(() => setProjects([]));
  }, [isSuperAdmin]);

  const filtered = !!(client || debouncedSearch || activeWithin || projectId);

  return (
    <Layout>
      <PageHeader
        title="User Activity"
        description="Who uses the mobile app, who the web dashboard, and on which version."
        actions={
          <Button
            variant="default"
            leftSection={<RefreshCw size={16} />}
            onClick={() => load()}
          >
            Refresh
          </Button>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <SegmentedControl
              value={client}
              onChange={(value) => {
                setClient(value);
                setPage(1);
              }}
              data={CLIENTS}
            />
            <Group gap="sm" wrap="wrap">
              <TextInput
                placeholder="Search name or email"
                aria-label="Search name or email"
                leftSection={<Search size={16} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                w={240}
              />
              <Select
                placeholder="Active any time"
                aria-label="Active within"
                data={ACTIVE_WITHIN}
                value={client === 'none' ? null : activeWithin}
                onChange={(value) => {
                  setActiveWithin(value);
                  setPage(1);
                }}
                disabled={client === 'none'}
                clearable
                w={180}
              />
              {isSuperAdmin && (
                <Select
                  placeholder="All projects"
                  aria-label="Project"
                  data={projects}
                  value={projectId}
                  onChange={(value) => {
                    setProjectId(value);
                    setPage(1);
                  }}
                  clearable
                  searchable
                  w={220}
                />
              )}
            </Group>
          </Group>

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<MonitorSmartphone size={28} />}
              title={filtered ? 'No users match these filters.' : 'No users yet.'}
              description={
                filtered
                  ? 'Try a different filter or search.'
                  : 'Active users appear here, with the app or web they use once they have been seen.'
              }
            />
          ) : (
            <>
              <Table.ScrollContainer minWidth={860}>
                <Table withTableBorder verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>User</Table.Th>
                      {isSuperAdmin && <Table.Th>Project</Table.Th>}
                      <Table.Th>Mobile app</Table.Th>
                      <Table.Th>Web dashboard</Table.Th>
                      <Table.Th>Last seen</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      // The row opens the user's profile, which leads back here.
                      <Table.Tr
                        key={row.employee_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`/employees/${row.employee_id}`, {
                            state: FROM_HERE,
                          })
                        }
                      >
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/employees/${row.employee_id}`}
                            state={FROM_HERE}
                            c="inherit"
                            size="sm"
                            fw={500}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {row.name || `Employee ${row.employee_id}`}
                          </Anchor>
                          <Text size="xs" c="dimmed">
                            {row.work_email || '—'}
                          </Text>
                        </Table.Td>
                        {isSuperAdmin && (
                          <Table.Td>
                            <Text size="sm">{row.project_name || '—'}</Text>
                            {row.company_name &&
                              row.company_name !== row.project_name && (
                                <Text size="xs" c="dimmed">
                                  {row.company_name}
                                </Text>
                              )}
                          </Table.Td>
                        )}
                        <Table.Td>
                          <ClientUsage client="app" usage={row.app} />
                        </Table.Td>
                        <Table.Td>
                          <ClientUsage client="web" usage={row.web} />
                        </Table.Td>
                        <Table.Td>
                          <SeenAt at={row.last_seen_at} size="sm" />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={totalItems}
                pageSize={PAGE_SIZE}
              />
            </>
          )}
        </Stack>
      </Paper>
    </Layout>
  );
};

export default ClientActivity;
